import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { AdminHttpService } from '../../../services/admin-http.service';
import { ToastService } from '../../../services/toast.service';
import { ParentCompanyStore } from '../../../stores/parent-company-store';
import { type ParentCompany } from '../../../models/parent-company';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Divider } from 'primeng/divider';
import { EmbarkSpinnerComponent } from '../../embark-spinner/embark-spinner.component';

@Component({
	selector: 'app-admin-actions',
	imports: [ButtonModule, ConfirmDialog, SelectModule, FormsModule, Divider, EmbarkSpinnerComponent],
	templateUrl: './admin-actions.component.html',
	styleUrl: './admin-actions.component.sass',
	providers: [ConfirmationService]
})
export class AdminActionsComponent {
	private adminHttpService = inject<AdminHttpService>(AdminHttpService);
	private toastService = inject<ToastService>(ToastService);
	private parentCompanyStore = inject<ParentCompanyStore>(ParentCompanyStore);
	private confirmationService =
		inject<ConfirmationService>(ConfirmationService);

	public ccRefreshLoading = false;
	public afeRefreshLoading = false;
	public buyerRefreshLoading = false;
	public buyerMigrationLoading = false;
	public siteRefreshLoading = false;
	public refreshRequisitionerLoading = false;
	public soGapMonitorLoading = false;

	public readonly buyerOptions = this.parentCompanyStore.parentCompanies;

	public selectedBuyer = signal<ParentCompany | null>(null);

	public refreshCostCenters(): void {
		this.ccRefreshLoading = true;
		this.adminHttpService.refreshCostCenters().subscribe({
			next: () => {
				this.ccRefreshLoading = false;
				this.toastService.showSuccessToast(
					'Cost center refresh started. This may take several minutes.'
				);
			},
			error: () => {
				this.ccRefreshLoading = false;
				this.toastService.showErrorToast(
					'Failed to start cost center refresh job.'
				);
			}
		});
	}

	public refreshAfes(): void {
		this.afeRefreshLoading = true;
		this.adminHttpService.refreshAfes().subscribe({
			next: () => {
				this.afeRefreshLoading = false;
				this.toastService.showSuccessToast(
					'AFE refresh started. This may take several minutes.'
				);
			},
			error: () => {
				this.afeRefreshLoading = false;
				this.toastService.showErrorToast('Failed to start AFE refresh job.');
			}
		});
	}

	public refreshBuyers(): void {
		this.buyerRefreshLoading = true;
		this.adminHttpService.refreshBuyers().subscribe({
			next: () => {
				this.buyerRefreshLoading = false;
				this.toastService.showSuccessToast('Buyers refreshed');
				this.parentCompanyStore.load(true);
			},
			error: () => {
				this.buyerRefreshLoading = false;
				this.toastService.showErrorToast('Failed to refresh Buyers.');
			}
		});
	}

	public buyerMigrationConfirmation(): void {
		this.selectedBuyer.set(null);

		this.confirmationService.confirm({
			key: 'migrationConfirmation',
			message: '',
			header: 'Migrate Buyer',
			rejectVisible: false,
			acceptLabel: 'OK',
			acceptButtonStyleClass: 'p-button-secondary',
			accept: () => {
				const buyer = this.selectedBuyer();
				if (!buyer) {
					this.toastService.showErrorToast('Select a buyer.');
					return;
				}

				this.buyerMigrationLoading = true;

				this.adminHttpService.migrateBuyer(buyer.name).subscribe({
					next: () => {
						this.buyerMigrationLoading = false;
						this.toastService.showSuccessToast(
							'Buyer migration started. This may take several minutes.'
						);
					},
					error: () => {
						this.buyerMigrationLoading = false;
						this.toastService.showErrorToast(
							'Failed to start buyer migration job.'
						);
					}
				});
			}
		});
	}

	public refreshBuyerSiteDepartments(): void {
		this.siteRefreshLoading = true;
		this.adminHttpService.syncBuyerSiteDepartments().subscribe({
			next: () => {
				this.siteRefreshLoading = false;
				this.toastService.showSuccessToast(
					'Buyer site departments refreshed.'
				);
				this.parentCompanyStore.load(true);
			},
			error: () => {
				this.siteRefreshLoading = false;
				this.toastService.showErrorToast(
					'Failed to refresh buyer site departments.'
				);
			}
		});
	}

	public runSalesOrderGapMonitor(): void {
		this.soGapMonitorLoading = true;
		this.adminHttpService.runSalesOrderGapMonitor().subscribe({
			next: () => {
				this.soGapMonitorLoading = false;
				this.toastService.showSuccessToast(
					'SO gap monitor started. This may take several minutes.'
				);
			},
			error: () => {
				this.soGapMonitorLoading = false;
				this.toastService.showErrorToast('Failed to start SO gap monitor.');
			}
		});
	}

	public refreshRequisitioners(): void {
		this.refreshRequisitionerLoading = true;
		this.adminHttpService.refreshOrderedByNames().subscribe({
			next: () => {
				this.refreshRequisitionerLoading = false;
				this.toastService.showSuccessToast(
					'Requisitioner refresh started. This may take several minutes.'
				);
			},
			error: () => {
				this.refreshRequisitionerLoading = false;
				this.toastService.showErrorToast(
					'Failed to start requisitioner refresh job.'
				);
			}
		});
	}


}
